import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Chip, EmptyState } from "@/components/Shared";
import { Eye, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import ParkingSettingsCard from "./ParkingSettingsCard";

const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);
const currentPeriod = () => new Date().toISOString().slice(0, 7);

export default function ParkingBillingTab({ settings, onSettingsSaved }) {
    const [period, setPeriod] = useState(currentPeriod());
    const [preview, setPreview] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [history, setHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(true);

    async function loadHistory() {
        setLoadingHistory(true);
        try {
            const { data } = await api.get("/parking/billing/history");
            setHistory(data || []);
        } catch (e) { toast.error(formatError(e)); }
        setLoadingHistory(false);
    }
    useEffect(() => { loadHistory(); }, []);

    async function runPreview() {
        setPreviewing(true);
        setPreview(null);
        try {
            const { data } = await api.get("/parking/billing/preview", { params: { period } });
            setPreview(data);
        } catch (e) { toast.error(formatError(e)); }
        setPreviewing(false);
    }

    async function runGenerate() {
        if (!preview || preview.to_create === 0) return;
        if (!window.confirm(`Generate ${preview.to_create} parking invoice(s) totalling ${inr(preview.total_amount)} for ${period}?`)) return;
        setGenerating(true);
        try {
            const { data } = await api.post("/parking/billing/generate", { period });
            toast.success(`Generated ${data.created_count} invoice(s) totalling ${inr(data.total_amount)}`);
            setPreview(null);
            loadHistory();
        } catch (e) { toast.error(formatError(e)); }
        setGenerating(false);
    }

    return (
        <div>
            <ParkingSettingsCard settings={settings} onSaved={onSettingsSaved} />

            <div className="bg-white border border-brand-line rounded-sm p-6 mb-6">
                <h3 className="font-heading text-lg text-brand-ink mb-1">Generate Parking Invoices</h3>
                <p className="text-xs text-brand-inkSoft mb-4">Preview before generating — invoices are only raised for allocations with charging enabled that are due for this billing period, and never duplicated.</p>
                <div className="flex items-end gap-3 mb-4">
                    <div className="space-y-2">
                        <Label>Billing period</Label>
                        <Input type="month" data-testid="billing-period-input" value={period} onChange={(e) => { setPeriod(e.target.value); setPreview(null); }} className="rounded-sm border-brand-line" />
                    </div>
                    <Button type="button" onClick={runPreview} disabled={previewing} data-testid="billing-preview-btn" variant="outline" className="rounded-full border-brand-line">
                        <Eye size={16} className="mr-2" /> {previewing ? "Previewing…" : "Preview"}
                    </Button>
                    {preview && (
                        <Button type="button" onClick={runGenerate} disabled={generating || preview.to_create === 0} data-testid="billing-generate-btn" className="rounded-full bg-brand-action hover:bg-brand-actionHover">
                            <ArrowsClockwise size={16} className="mr-2" /> {generating ? "Generating…" : `Generate ${preview.to_create} invoice(s)`}
                        </Button>
                    )}
                </div>

                {preview && (
                    <div data-testid="billing-preview-result">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                            <div className="bg-brand-bg rounded-sm p-3"><p className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Chargeable allocations</p><p className="font-heading text-xl text-brand-ink">{preview.chargeable_allocations}</p></div>
                            <div className="bg-[#ECFDF5] rounded-sm p-3"><p className="text-[10px] uppercase tracking-overline text-[#059669]">To create</p><p className="font-heading text-xl text-[#059669]">{preview.to_create}</p></div>
                            <div className="bg-[#FFF7ED] rounded-sm p-3"><p className="text-[10px] uppercase tracking-overline text-[#EA580C]">Already invoiced / skipped</p><p className="font-heading text-xl text-[#EA580C]">{preview.already_invoiced + preview.exceptions_count}</p></div>
                            <div className="bg-brand-sage/30 rounded-sm p-3"><p className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Total amount</p><p className="font-heading text-xl text-brand-ink">{inr(preview.total_amount)}</p></div>
                        </div>
                        {preview.rows.length > 0 && (
                            <div className="mb-4">
                                <p className="text-xs font-medium text-brand-ink mb-2">Invoices to be created</p>
                                <div className="overflow-x-auto border border-brand-line rounded-sm">
                                    <table className="w-full text-sm" data-testid="billing-to-create-table">
                                        <thead><tr className="text-left text-[10px] uppercase tracking-overline text-brand-inkSoft border-b border-brand-line"><th className="py-2 px-3">Flat</th><th className="py-2 px-3">Slot</th><th className="py-2 px-3">Amount</th></tr></thead>
                                        <tbody>{preview.rows.map((r) => (
                                            <tr key={r.allocation_id} className="border-b border-brand-line/60 last:border-0">
                                                <td className="py-2 px-3">{r.flat_number || "—"}</td><td className="py-2 px-3">{r.slot_number}</td><td className="py-2 px-3">{inr(r.amount)}</td>
                                            </tr>
                                        ))}</tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                        {preview.exceptions.length > 0 && (
                            <div>
                                <p className="text-xs font-medium text-brand-ink mb-2">Skipped / exceptions</p>
                                <div className="overflow-x-auto border border-brand-line rounded-sm">
                                    <table className="w-full text-sm" data-testid="billing-exceptions-table">
                                        <thead><tr className="text-left text-[10px] uppercase tracking-overline text-brand-inkSoft border-b border-brand-line"><th className="py-2 px-3">Flat</th><th className="py-2 px-3">Slot</th><th className="py-2 px-3">Reason</th></tr></thead>
                                        <tbody>{preview.exceptions.map((r, i) => (
                                            <tr key={i} className="border-b border-brand-line/60 last:border-0">
                                                <td className="py-2 px-3">{r.flat_number || "—"}</td><td className="py-2 px-3">{r.slot_number}</td><td className="py-2 px-3 text-brand-inkSoft">{r.reason}</td>
                                            </tr>
                                        ))}</tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="bg-white border border-brand-line rounded-sm p-6">
                <h3 className="font-heading text-lg text-brand-ink mb-4">Billing history</h3>
                {loadingHistory ? (
                    <p className="text-sm text-brand-inkSoft py-4">Loading…</p>
                ) : history.length === 0 ? (
                    <EmptyState title="No parking invoices generated yet" />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="billing-history-table">
                            <thead><tr className="text-left text-[10px] uppercase tracking-overline text-brand-inkSoft border-b border-brand-line">
                                <th className="py-2 px-3">Receipt</th><th className="py-2 px-3">Flat</th><th className="py-2 px-3">Slot</th><th className="py-2 px-3">Period</th><th className="py-2 px-3">Amount</th><th className="py-2 px-3">Status</th>
                            </tr></thead>
                            <tbody>{history.map((r) => (
                                <tr key={r.id} className="border-b border-brand-line/60 last:border-0" data-testid={`billing-history-row-${r.id}`}>
                                    <td className="py-2 px-3"><Link to={`/app/invoices/${r.id}`} className="text-brand-action hover:underline font-mono">{r.receipt_number}</Link></td>
                                    <td className="py-2 px-3">{r.flat_number || "—"}</td>
                                    <td className="py-2 px-3">{r.parking_slot_number || "—"}</td>
                                    <td className="py-2 px-3">{r.month}</td>
                                    <td className="py-2 px-3">{inr(r.amount)}</td>
                                    <td className="py-2 px-3"><Chip variant={r.payment_status === "paid" ? "success" : r.payment_status === "partial" ? "warn" : "neutral"}>{r.payment_status}</Chip></td>
                                </tr>
                            ))}</tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
